import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { live } from 'lit/directives/live.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'
import type { PropertyValues } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import {
	WithInternals,
	WithFocus,
	internals,
	compose,
	anchor
} from '#utils/mixins'

import styles from './lfds-input.css.ts'

const DELEGATED_EVENTS = [
	'input',
	'change',
	'focus',
	'blur',
	'focusin',
	'focusout',
	'keydown',
	'keyup',
	'keypress',
	'paste',
	'cut',
	'copy',
	'select',
	'reset'
]

/**
 * @name LfdsInput
 *
 * @tag lfds-input
 * @status ready
 * @slot label - Input label
 * @slot start - Before the input. Useful for icons and buttons.
 * @slot end - After the input. Useful for icons and buttons.
 */
export default class LfdsInput extends compose(
	LfuiElement,
	WithInternals<HTMLInputElement>,
	WithFocus
) {
	static styles = [LfuiElement.styles, styles]

	// React's synthetic `onChange` maps to the DOM `input` event for built-in
	// inputs. By declaring `change` here, @lit/react attaches a native listener,
	// so consumers using <LfdsInput onChange={…}> get the real DOM change event.
	// React 19 improved custom-element handling, but @lit/react remains the
	// recommended path for consistent behavior across React versions.
	static events = ['change']

	@property() value: string | number | File = ''
	@property() type:
		| 'color'
		| 'date'
		| 'datetime-local'
		| 'email'
		| 'file'
		| 'month'
		| 'number'
		| 'password'
		| 'search'
		| 'tel'
		| 'text'
		| 'time'
		| 'url'
		| 'week'
	@property() inputmode:
		| 'none'
		| 'text'
		| 'tel'
		| 'url'
		| 'email'
		| 'numeric'
		| 'decimal'
		| 'search'
	@property({ type: Number }) maxlength: number
	@property({ type: Number }) minlength: number
	@property() autocomplete: 'on' | 'off'
	@property() dirname: string
	@property() label!: string
	@property() min: string
	@property() max: string
	@property() name: string
	@property() list: string
	@property({ type: Number }) step: number
	@property({ attribute: 'help-text' }) helpText: string
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'start-text' }) startText: string
	@property({ attribute: 'end-text' }) endText: string
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) readonly: boolean = false
	@property({ type: Boolean }) autofocus: boolean = false

	/**
	 * Remember to set `validation-message` if the input is invalid,
	 * as it is both conveyed to the user and used for the input's setCustomValidity method.
	 */
	@property({ type: Boolean }) invalid: boolean

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#labels = refCollection()
	#descriptions = refCollection()

	constructor() {
		super()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()

		// Prevent the host (custom element) from being exposed as a separate node
		// in the accessibility tree. The inner native <input> carries the name,
		// role and value.
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')

		this[internals].form?.addEventListener('reset', () => {
			this.invalid = false
			this.value = ''
		})

		// Handle calling reportValidity on parent form
		this.addEventListener('invalid', () => {
			this.invalid = true
		})

		// Allow implicit submit when Enter is pressed inside the input.
		// Note: delegateEvents stops native keydown from bubbling out of the
		// shadow root and re-dispatches a CustomEvent from the host instead.
		// Listening on the host lets us catch that delegated event.
		this.addEventListener('keydown', this.#onHostKeydown)
	}

	disconnectedCallback() {
		this.removeEventListener('keydown', this.#onHostKeydown)
		super.disconnectedCallback()
	}

	focus() {
		this[anchor].value?.focus()
	}

	select() {
		this[anchor].value?.select()
	}

	setSelectionRange(start: number, end: number) {
		this[anchor].value?.setSelectionRange(start, end)
	}

	stepDown() {
		this[anchor].value?.stepDown()
	}

	stepUp() {
		this[anchor].value?.stepUp()
	}

	updated(changed: PropertyValues<this>) {
		if (changed.has('value')) {
			const data = new FormData()
			const value =
				typeof this.value === 'number' ? String(this.value) : this.value
			data.set(this.name, value)
			this[internals].setFormValue(data)
		}

		const input = this[anchor].value
		if (!input) return

		const { minlength } = this
		if (this.validationMessage) {
			this[internals].setValidity(input.validity, this.validationMessage, input)
		} else if (changed.has('value') || minlength) {
			// Native minlength only blocks form submission, not typing, so we still
			// handle it manually to surface a custom message while the user types.
			const value = String(this.value || '')
			let validity = input.validity
			let message = this.validationMessage || input.validationMessage
			if (value.length < minlength) {
				validity = { tooShort: true } as ValidityState
				message = `För få tecken: ${minlength - value.length}`
			}
			this[internals].setValidity(validity, message, input)
		}
	}

	#oninput = (event: Event) => {
		this.value = (event.target as HTMLInputElement).value
		this.invalid = false
	}

	#onkeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' && !event.defaultPrevented) {
			event.preventDefault()
			this[internals].form?.requestSubmit()
		}
	}

	#onHostKeydown = (event: KeyboardEvent | CustomEvent<KeyboardEvent>) => {
		// delegateEvents dispatches a CustomEvent from the host where .detail holds
		// the original KeyboardEvent. Fall back to the event itself if present.
		const originalEvent =
			(event as CustomEvent<KeyboardEvent>).detail || (event as KeyboardEvent)
		this.#onkeydown(originalEvent)
	}

	#onclick() {
		this[anchor].value?.select()
	}

	render() {
		const value = String(this.value ?? '')
		const charsCount = value.length
		const charsLeft = this.maxlength - charsCount
		const ariaLabel = this.srLabel ?? undefined

		return html`
			<div class="header">
				<div class="header-text">
					<label
						class="label"
						for="input"
						${this.#labels.add()}
						@click=${this.#onclick}
					>
						<slot name="label">${this.label}</slot>
					</label>

					${this.helpText
						? html`
								<span class="help description" ${this.#descriptions.add()}>
									${this.helpText}
								</span>
							`
						: nothing}
				</div>

				${this.maxlength
					? html`
							<span
								class=${classMap({
									help: true,
									counter: true,
									warning: charsCount < this.minlength
								})}
							>
								<span aria-hidden="true">
									${charsCount}/${this.maxlength}
								</span>
								<span class="hidden" ${this.#labels.add()}>
									<span aria-live="polite">
										${charsLeft < 6 && charsLeft > 0
											? `Antal tecken kvar: ${charsLeft}`
											: nothing}
									</span>
									<span aria-live="assertive">
										${charsLeft === 0 ? 'Max antal tecken' : nothing}
									</span>
								</span>
							</span>
						`
					: nothing}
			</div>
			<div
				class=${classMap({
					box: true,
					invalid: this.invalid,
					['focus-visible']: this.focusVisible
				})}
			>
				<slot name="start">
					${this.startText
						? html`
								<span
									class="affix prefix"
									aria-hidden="true"
									@click=${this.#onclick}
								>
									${this.startText}
								</span>
							`
						: nothing}
				</slot>
				<input
					id="input"
					class="input"
					placeholder=" "
					aria-invalid=${ifDefined(this.invalid ? 'true' : undefined)}
					aria-label=${ifDefined(ariaLabel)}
					autocomplete=${ifDefined(this.autocomplete)}
					dirname=${ifDefined(this.dirname)}
					inputmode=${ifDefined(this.inputmode)}
					max=${ifDefined(this.max)}
					maxlength=${ifDefined(this.maxlength)}
					min=${ifDefined(this.min)}
					step=${ifDefined(this.step)}
					list=${ifDefined(this.list)}
					name=${ifDefined(this.name)}
					type=${ifDefined(this.type)}
					?autofocus=${this.autofocus}
					?required=${this.required}
					?readonly=${this.readonly}
					@input=${this.#oninput}
					.value=${live(value)}
					${ref(this[anchor])}
					${this.#labels.bind('ariaLabelledByElements')}
					${this.#descriptions.bind('ariaDescribedByElements')}
					${delegateEvents(DELEGATED_EVENTS, {
						before: { input: this.#oninput }
					})}
					${ariaElements({
						describedBy: this.srDescribedby,
						labelledBy: this.srLabelledby,
						controls: this.srControls
					})}
				/>
				<slot name="end">
					${this.endText
						? html`
								<span
									class="affix suffix"
									aria-hidden="true"
									@click=${this.#onclick}
								>
									${this.endText}
								</span>
							`
						: nothing}
				</slot>
			</div>
			${this.validationMessage
				? html`
						<div aria-atomic="true" role="alert" class="validation">
							<span class="icon">
								<lfds-icon
									icon-id="alert"
									size="24"
									color="currentcolor"
								></lfds-icon>
							</span>
							<span ${this.#labels.add()}>${this.validationMessage}</span>
						</div>
					`
				: nothing}
		`
	}
}
